type Comparator<T_ITEM, T_VALUE = T_ITEM> = (value1: T_VALUE, value2: T_ITEM) => number;
type WeighGetter<T_ITEM> = (value: T_ITEM) => number | string;

/**
 * Uses a binary search to determine the index at which `value`
 * should be inserted into `array` in order to maintain its sort order.
 * Accepts `comparator` which is invoked for `value` and each element
 * of `array` to compare their.
 *
 * Inspired by _.sortedIndexBy (from lodash), but allows comparing by many fields.
 *
 * @param {T_ITEM[]} array The sorted array to inspect.
 * @param {T_VALUE} value The value to search for.
 * @param {((value1: T_ITEM, value2: T_VALUE) => number)} comparator The comparator invoked per element.
 * @param {boolean} [retHighest] Specify returning the highest qualified index.
 * @returns {number} Returns the index at which `value` should be inserted into `array`.
 */
export function sortedIndexByComparator<T_ITEM, T_VALUE = T_ITEM>(
    array: ArrayLike<T_ITEM>,
    value: T_VALUE,
    comparator: Comparator<T_ITEM, T_VALUE>,
    retHighest?: boolean
): number {
    let low = 0;
    let high = array && array.length || 0;

    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        const comparisonValue = comparator(value, array[mid]);

        const setLow = retHighest
            ? comparisonValue >= 0
            : comparisonValue > 0;

        if (setLow) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return high;
}

/**
 * Uses a binary search to determine the index at which `value`
 * should be inserted into `array` in order to maintain its sort order.
 * Accepts `weighGetters` which is array of functions, each is called
 * in series for elements of the array, the results are compared.
 *
 * Inspired by _.sortedIndexBy (from lodash), but allows comparing by many fields.
 *
 * @param {T_ITEM[]} array The sorted array to inspect.
 * @param {T_ITEM} value The value to search for.
 * @param {((value1: T_ITEM) => number | string)[]} weighGetters
 * @param {boolean} [retHighest] Specify returning the highest qualified index.
 * @returns {number} Returns the index at which `value` should be inserted into `array`.
 */
export function sortedIndexByWeightGetters<T_ITEM>(
    array: ArrayLike<T_ITEM>,
    value: T_ITEM,
    weighGetters: Array<WeighGetter<T_ITEM>>,
    retHighest?: boolean
): number {
    return sortedIndexByComparator<T_ITEM, T_ITEM>(
        array,
        value,
        (value1, value2) => {
            let comparisonValue: number = 0;

            for (const weighGetter of weighGetters) {
                const weight1 = weighGetter(value1);
                const weight2 = weighGetter(value2);

                comparisonValue = weight1 > weight2 ? 1 : weight1 < weight2 ? -1 : 0;

                if (comparisonValue !== 0) {
                    break;
                }
            }

            return comparisonValue;
        },
        retHighest
    );
}
